/*
	jsrepo 1.41.2
	Installed from https://reactbits.dev/default/
	2-26-2025
*/

import { useEffect, useRef, useState, useCallback } from 'react';
import { mat4, quat, vec2, vec3 } from 'gl-matrix';
import './InfiniteMenu.css';
import React from 'react';
import { Card, ButtonGroup, Button } from '@geist-ui/core';

// CSS inline per rimuovere le animazioni dei bottoni ma mantenere la transizione del tema
const noHoverEffectStyle = {
  transform: 'none !important',
  animation: 'none !important',
  boxShadow: 'none !important'
};

const discVertShaderSource = `#version 300 es

uniform mat4 uWorldMatrix;
uniform mat4 uViewMatrix;
uniform mat4 uProjectionMatrix;
uniform vec3 uCameraPosition;
uniform vec4 uRotationAxisVelocity;
uniform bool uIsDarkTheme;

in vec3 aModelPosition;
in vec3 aModelNormal;
in vec2 aModelUvs;
in mat4 aInstanceMatrix;

out vec2 vUvs;
out float vAlpha;
flat out int vInstanceId;

#define PI 3.141593

void main() {
    vec4 worldPosition = uWorldMatrix * aInstanceMatrix * vec4(aModelPosition, 1.);

    // center of the disc in world space
    vec3 centerPos = (uWorldMatrix * aInstanceMatrix * vec4(0., 0., 0., 1.)).xyz;
    float radius = length(centerPos.xyz);

    // skip the center vertex of the disc geometry
    if (gl_VertexID > 0) {
        // stretch the disc according to the axis and velocity of the rotation
        vec3 rotationAxis = uRotationAxisVelocity.xyz;
        float rotationVelocity = min(.15, uRotationAxisVelocity.w * 15.);
        // the stretch direction is orthogonal to the rotation axis and the position
        vec3 stretchDir = normalize(cross(centerPos, rotationAxis));
        // the position of this vertex relative to the center position
        vec3 relativeVertexPos = normalize(worldPosition.xyz - centerPos);
        // vertices more in line with the stretch direction get a larger offset
        float strength = dot(stretchDir, relativeVertexPos);
        float invAbsStrength = min(0., abs(strength) - 1.);
        strength = rotationVelocity * sign(strength) * abs(invAbsStrength * invAbsStrength * invAbsStrength + 1.);
        // apply the stretch distortion
        worldPosition.xyz += stretchDir * strength;
    }

    // move the vertex back to the overall sphere
    worldPosition.xyz = radius * normalize(worldPosition.xyz);

    gl_Position = uProjectionMatrix * uViewMatrix * worldPosition;

    // Calcola un alpha diverso a seconda del tema
    // Tema chiaro: usa un range più stretto per meno trasparenza
    // Tema scuro: usa un range più ampio per maggiore trasparenza
    float baseAlpha = uIsDarkTheme 
        ? smoothstep(0.5, 1., normalize(worldPosition.xyz).z) * .9 + .1  // Tema scuro: più trasparente
        : smoothstep(0.3, 0.8, normalize(worldPosition.xyz).z) * .7 + .3; // Tema chiaro: meno trasparente
    
    vAlpha = baseAlpha;
    vUvs = aModelUvs;
    vInstanceId = gl_InstanceID;
}
`;

const discFragShaderSource = `#version 300 es
precision highp float;

uniform sampler2D uTex;
uniform int uItemCount;
uniform int uAtlasSize;
uniform bool uIsDarkTheme;

out vec4 outColor;

in vec2 vUvs;
in float vAlpha;
flat in int vInstanceId;

void main() {
    // Calculate which item to display based on instance ID
    int itemIndex = vInstanceId % uItemCount;
    int cellsPerRow = uAtlasSize;
    int cellX = itemIndex % cellsPerRow;
    int cellY = itemIndex / cellsPerRow;
    vec2 cellSize = vec2(1.0) / vec2(float(cellsPerRow));
    vec2 cellOffset = vec2(float(cellX), float(cellY)) * cellSize;

    // Get texture dimensions and calculate aspect ratio
    ivec2 texSize = textureSize(uTex, 0);
    float imageAspect = float(texSize.x) / float(texSize.y);
    float containerAspect = 1.0; // Assuming square container
    
    // Calculate cover scale factor
    float scale = max(imageAspect / containerAspect, 
                     containerAspect / imageAspect);
    
    // Rotate 180 degrees and adjust UVs for cover
    vec2 st = vec2(vUvs.x, 1.0 - vUvs.y);
    st = (st - 0.5) * scale + 0.5;
    
    // Clamp coordinates to prevent repeating
    st = clamp(st, 0.0, 1.0);
    
    // Map to the correct cell in the atlas
    st = st * cellSize + cellOffset;
    
    vec4 texColor = texture(uTex, st);
    
    // Adattamento specifico al tema
    if (uIsDarkTheme) {
        // Nel tema scuro amplifichiamo leggermente l'intensità del colore
        // per renderlo più visibile sullo sfondo scuro
        outColor = vec4(texColor.rgb * 1.1, texColor.a * vAlpha);
    } else {
        // Nel tema chiaro lasciamo il colore naturale ma amplifichiamo leggermente
        // l'opacità per renderlo meno trasparente
        outColor = vec4(texColor.rgb, texColor.a * (vAlpha * 0.9 + 0.1));
    }
}
`;

class Face {
  /**
   * Creates a new triangle face by the indices of each vertex.
   * @param {number} a Index of the first vertex
   * @param {number} b Index of the second vertex
   * @param {number} c Index of the third vertex
   */
  constructor(a, b, c) {
    this.a = a;
    this.b = b;
    this.c = c;
  }
}

class Vertex {
  constructor(x, y, z) {
    this.position = vec3.fromValues(x, y, z);
    this.normal = vec3.create();
    this.uv = vec2.create();
  }
}

class Geometry {
  constructor() {
    this.vertices = [];
    this.faces = [];
  }

  addVertex(...args) {
    for (let i = 0; i < args.length; i += 3) {
      this.vertices.push(new Vertex(args[i], args[i + 1], args[i + 2]));
    }
    return this;
  }

  addFace(...args) {
    for (let i = 0; i < args.length; i += 3) {
      this.faces.push(new Face(args[i], args[i + 1], args[i + 2]));
    }
    return this;
  }

  get lastVertex() {
    return this.vertices[this.vertices.length - 1];
  }

  subdivide(divisions = 1) {
    const midPointCache = {};
    let f = this.faces;

    for (let div = 0; div < divisions; ++div) {
      const newFaces = new Array(f.length * 4);

      f.forEach((face, ndx) => {
        const mAB = this.getMidPoint(face.a, face.b, midPointCache);
        const mBC = this.getMidPoint(face.b, face.c, midPointCache);
        const mCA = this.getMidPoint(face.c, face.a, midPointCache);

        const i = ndx * 4;
        newFaces[i + 0] = new Face(face.a, mAB, mCA);
        newFaces[i + 1] = new Face(face.b, mBC, mAB);
        newFaces[i + 2] = new Face(face.c, mCA, mBC);
        newFaces[i + 3] = new Face(mAB, mBC, mCA);
      });

      f = newFaces;
    }

    this.faces = f;
    return this;
  }

  spherize(radius = 1) {
    this.vertices.forEach((vertex) => {
      vec3.normalize(vertex.normal, vertex.position);
      vec3.scale(vertex.position, vertex.normal, radius);
    });
    return this;
  }

  get data() {
    return {
      vertices: this.vertexData,
      indices: this.indexData,
      normals: this.normalData,
      uvs: this.uvData,
    };
  }

  get vertexData() {
    return new Float32Array(this.vertices.flatMap((v) => Array.from(v.position)));
  }

  get normalData() {
    return new Float32Array(this.vertices.flatMap((v) => Array.from(v.normal)));
  }

  get uvData() {
    return new Float32Array(this.vertices.flatMap((v) => Array.from(v.uv)));
  }

  get indexData() {
    return new Uint16Array(this.faces.flatMap((f) => [f.a, f.b, f.c]));
  }

  getMidPoint(ndxA, ndxB, cache) {
    const cacheKey = ndxA < ndxB ? `k_${ndxB}_${ndxA}` : `k_${ndxA}_${ndxB}`;
    if (Object.prototype.hasOwnProperty.call(cache, cacheKey)) {
      return cache[cacheKey];
    }
    const a = this.vertices[ndxA].position;
    const b = this.vertices[ndxB].position;
    const ndx = this.vertices.length;
    cache[cacheKey] = ndx;
    this.addVertex(
      (a[0] + b[0]) * 0.5,
      (a[1] + b[1]) * 0.5,
      (a[2] + b[2]) * 0.5
    );
    return ndx;
  }
}

class IcosahedronGeometry extends Geometry {
  constructor() {
    super();
    const t = Math.sqrt(5) * 0.5 + 0.5;
    this.addVertex(
      -1, t, 0, 1, t, 0, -1, -t, 0, 1, -t, 0,
      0, -1, t, 0, 1, t, 0, -1, -t, 0, 1, -t,
      t, 0, -1, t, 0, 1, -t, 0, -1, -t, 0, 1
    ).addFace(
      0, 11, 5, 0, 5, 1, 0, 1, 7, 0, 7, 10, 0, 10, 11,
      1, 5, 9, 5, 11, 4, 11, 10, 2, 10, 7, 6, 7, 1, 8,
      3, 9, 4, 3, 4, 2, 3, 2, 6, 3, 6, 8, 3, 8, 9,
      4, 9, 5, 2, 4, 11, 6, 2, 10, 8, 6, 7, 9, 8, 1
    );
  }
}

class DiscGeometry extends Geometry {
  constructor(steps = 4, radius = 1) {
    super();
    steps = Math.max(4, steps);

    const alpha = (2 * Math.PI) / steps;

    // center vertex
    this.addVertex(0, 0, 0);
    this.lastVertex.uv[0] = 0.5;
    this.lastVertex.uv[1] = 0.5;

    for (let i = 0; i < steps; ++i) {
      const x = Math.cos(alpha * i);
      const y = Math.sin(alpha * i);
      this.addVertex(radius * x, radius * y, 0);
      this.lastVertex.uv[0] = x * 0.5 + 0.5;
      this.lastVertex.uv[1] = y * 0.5 + 0.5;

      if (i > 0) {
        this.addFace(0, i, i + 1);
      }
    }
    this.addFace(0, steps, 1);
  }
}

function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);

  if (success) {
    return shader;
  }

  console.error(gl.getShaderInfoLog(shader));
  gl.deleteShader(shader);
  return null;
}

function createProgram(gl, shaderSources, transformFeedbackVaryings, attribLocations) {
  const program = gl.createProgram();

  [gl.VERTEX_SHADER, gl.FRAGMENT_SHADER].forEach((type, ndx) => {
    const shader = createShader(gl, type, shaderSources[ndx]);
    if (shader) gl.attachShader(program, shader);
  });

  if (transformFeedbackVaryings) {
    gl.transformFeedbackVaryings(program, transformFeedbackVaryings, gl.SEPARATE_ATTRIBS);
  }

  if (attribLocations) {
    for (const attrib in attribLocations) {
      gl.bindAttribLocation(program, attribLocations[attrib], attrib);
    }
  }

  gl.linkProgram(program);
  const success = gl.getProgramParameter(program, gl.LINK_STATUS);

  if (success) {
    return program;
  }

  console.error(gl.getProgramInfoLog(program));
  gl.deleteProgram(program);
  return null;
}

function makeVertexArray(gl, bufLocNumElmPairs, indices) {
  const va = gl.createVertexArray();
  gl.bindVertexArray(va);

  for (const [buffer, loc, numElem] of bufLocNumElmPairs) {
    if (loc === -1) continue;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(
      loc,
      numElem,
      gl.FLOAT,
      false,
      0,
      0
    );
  }

  if (indices) {
    const indexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);
  }

  gl.bindVertexArray(null);
  return va;
}

function resizeCanvasToDisplaySize(canvas) {
  const dpr = Math.min(2, window.devicePixelRatio);
  const displayWidth = Math.round(canvas.clientWidth * dpr);
  const displayHeight = Math.round(canvas.clientHeight * dpr);
  const needResize = canvas.width !== displayWidth || canvas.height !== displayHeight;
  if (needResize) {
    canvas.width = displayWidth;
    canvas.height = displayHeight;
  }
  return needResize;
}

function makeBuffer(gl, sizeOrData, usage) {
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, sizeOrData, usage);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return buf;
}

function createAndSetupTexture(gl, minFilter, magFilter, wrapS, wrapT) {
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrapS);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrapT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minFilter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, magFilter);
  return texture;
}

class ArcballControl {
  // flag which indicates if the user is currently dragging
  isPointerDown = false;

  // orientation of the object
  orientation = quat.create();

  // current pointer rotation as a quaternion
  pointerRotation = quat.create();

  // velocity of rotation
  rotationVelocity = 0;

  // rotation axis
  rotationAxis = vec3.fromValues(1, 0, 0);

  // direction to move the snap target to (in world space)
  snapDirection = vec3.fromValues(0, 0, -1);

  // direction of the target to move to the snap direction (in world space)
  snapTargetDirection;

  EPSILON = 0.1;
  IDENTITY_QUAT = quat.create();

  constructor(canvas, updateCallback) {
    this.canvas = canvas;
    this.updateCallback = updateCallback || (() => null);

    this.pointerPos = vec2.create();
    this.previousPointerPos = vec2.create();
    this._rotationVelocity = 0; // smooth rotational velocity
    this._combinedQuat = quat.create();

    canvas.addEventListener('pointerdown', (e) => {
      vec2.set(this.pointerPos, e.clientX, e.clientY);
      vec2.copy(this.previousPointerPos, this.pointerPos);
      this.isPointerDown = true;
    });
    canvas.addEventListener('pointerup', () => {
      this.isPointerDown = false;
    });
    canvas.addEventListener('pointerleave', () => {
      this.isPointerDown = false;
    });
    canvas.addEventListener('pointermove', (e) => {
      if (this.isPointerDown) {
        vec2.set(this.pointerPos, e.clientX, e.clientY);
      }
    });

    canvas.style.touchAction = 'none';
  }

  update(deltaTime, targetFrameDuration = 16) {
    const timeScale = deltaTime / targetFrameDuration + 0.00001;
    let angleFactor = timeScale;
    let snapRotation = quat.create();

    if (this.isPointerDown) {
      const INTENSITY = 0.3 * timeScale;
      const ANGLE_AMPLIFICATION = 5 / timeScale;

      const midPointerPos = vec2.sub(vec2.create(), this.pointerPos, this.previousPointerPos);
      vec2.scale(midPointerPos, midPointerPos, INTENSITY);

      if (vec2.sqrLen(midPointerPos) > this.EPSILON) {
        vec2.add(midPointerPos, this.previousPointerPos, midPointerPos);

        const p = this.#project(midPointerPos);
        const q = this.#project(this.previousPointerPos);
        const a = vec3.normalize(vec3.create(), p);
        const b = vec3.normalize(vec3.create(), q);

        vec2.copy(this.previousPointerPos, midPointerPos);

        angleFactor *= ANGLE_AMPLIFICATION;

        this.quatFromVectors(a, b, this.pointerRotation, angleFactor);
      } else {
        quat.slerp(this.pointerRotation, this.pointerRotation, this.IDENTITY_QUAT, INTENSITY);
      }
    } else {
      const INTENSITY = 0.1 * timeScale;
      quat.slerp(this.pointerRotation, this.pointerRotation, this.IDENTITY_QUAT, INTENSITY);

      if (this.snapTargetDirection) {
        const SNAPPING_INTENSITY = 0.2;
        const a = this.snapTargetDirection;
        const b = this.snapDirection;
        const sqrDist = vec3.squaredDistance(a, b);
        const distanceFactor = Math.max(0.1, 1 - sqrDist * 10);
        angleFactor *= SNAPPING_INTENSITY * distanceFactor;
        this.quatFromVectors(a, b, snapRotation, angleFactor);
      }
    }

    const combinedQuat = quat.multiply(quat.create(), snapRotation, this.pointerRotation);
    this.orientation = quat.multiply(quat.create(), combinedQuat, this.orientation);
    quat.normalize(this.orientation, this.orientation);

    const RA_INTENSITY = 0.8 * timeScale;
    quat.slerp(this._combinedQuat, this._combinedQuat, combinedQuat, RA_INTENSITY);
    quat.normalize(this._combinedQuat, this._combinedQuat);

    const rad = Math.acos(this._combinedQuat[3]) * 2.0;
    const s = Math.sin(rad / 2.0);
    let rv = 0;
    if (s > 0.000001) {
      rv = rad / (2 * Math.PI);
      this.rotationAxis[0] = this._combinedQuat[0] / s;
      this.rotationAxis[1] = this._combinedQuat[1] / s;
      this.rotationAxis[2] = this._combinedQuat[2] / s;
    }

    const RV_INTENSITY = 0.5 * timeScale;
    this._rotationVelocity += (rv - this._rotationVelocity) * RV_INTENSITY;
    this.rotationVelocity = this._rotationVelocity / timeScale;

    this.updateCallback(deltaTime);
  }

  quatFromVectors(a, b, out, angleFactor = 1) {
    const axis = vec3.cross(vec3.create(), a, b);
    vec3.normalize(axis, axis);
    const d = Math.max(-1, Math.min(1, vec3.dot(a, b)));
    const angle = Math.acos(d) * angleFactor;
    quat.setAxisAngle(out, axis, angle);
    return { q: out, axis, angle };
  }

  #project(pos) {
    const r = 2;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const s = Math.max(w, h) - 1;

    // map to [-1, 1]
    const x = (2 * pos[0] - w - 1) / s;
    const y = (2 * pos[1] - h - 1) / s;
    let z = 0;
    const xySq = x * x + y * y;
    const rSq = r * r;

    if (xySq <= rSq / 2.0) {
      z = Math.sqrt(rSq - xySq);
    } else {
      z = rSq / Math.sqrt(xySq);
    }
    return vec3.fromValues(-x, y, z);
  }
}

class InfiniteGridMenu {
  TARGET_FRAME_DURATION = 1000 / 60; // 60 fps
  SPHERE_RADIUS = 2;

  #time = 0;
  #deltaTime = 0;
  #deltaFrames = 0;
  #frames = 0;
  
  // Aggiungi proprietà per l'animazione di interpolazione
  #isAnimatingToInitial = false;
  #animationProgress = 0;
  #animationDuration = 800; // Durata dell'animazione in millisecondi
  #startOrientation = null;
  #targetOrientation = null;

  camera = {
    matrix: mat4.create(),
    near: 0.1,
    far: 40,
    fov: Math.PI / 4,
    aspect: 1,
    position: vec3.fromValues(0, 0, 3),
    up: vec3.fromValues(0, 1, 0),
    matrices: {
      view: mat4.create(),
      projection: mat4.create(),
      inversProjection: mat4.create(),
    },
  };

  nearestVertexIndex = null;
  smoothRotationVelocity = 0;
  scaleFactor = 1.0; // default
  movementActive = false;

  constructor(canvas, items, onActiveItemChange, onMovementChange, onInit = null, isDarkTheme = false) {
    this.canvas = canvas;
    this.items = items || [];
    this.onActiveItemChange = onActiveItemChange || (() => { });
    this.onMovementChange = onMovementChange || (() => { });
    this.isDarkTheme = isDarkTheme;
    this.#init(onInit);
  }

  resize() {
    this.viewportSize = vec2.set(
      this.viewportSize || vec2.create(),
      this.canvas.clientWidth,
      this.canvas.clientHeight
    );

    const gl = this.gl;
    const needsResize = resizeCanvasToDisplaySize(gl.canvas);
    if (needsResize) {
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    }

    this.#updateProjectionMatrix(gl);
  }

  run(time = 0) {
    this.#deltaTime = Math.min(32, time - this.#time);
    this.#time = time;
    this.#deltaFrames = this.#deltaTime / this.TARGET_FRAME_DURATION;
    this.#frames += this.#deltaFrames;

    this.#animate(this.#deltaTime);
    this.#render();

    // Se è la prima esecuzione, assicurati che il primo elemento sia attivo
    if (this.firstRun) {
      this.firstRun = false;
      // Attiva il primo elemento (la texture selezionata)
      if (this.onActiveItemChange && this.items.length > 0) {
        this.onActiveItemChange(0);
      }
      
      // Orienta la sfera in modo che il primo vertice sia di fronte
      if (this.instancePositions.length > 0) {
        // Imposta il punto di snap alla posizione del primo vertice
        const firstVertexPos = this.instancePositions[0];
        this.control.snapDirection = vec3.normalize(vec3.create(), firstVertexPos);
      }
    }

    requestAnimationFrame((t) => this.run(t));
  }

  #init(onInit) {
    this.gl = this.canvas.getContext('webgl2', { antialias: true, alpha: true });
    const gl = this.gl;
    if (!gl) {
      throw new Error('No WebGL 2 context!');
    }

    this.firstRun = true; // Flag per il primo rendering
    this.firstRunUpdate = true; // Flag per forzare l'elemento iniziale
    this.viewportSize = vec2.fromValues(this.canvas.clientWidth, this.canvas.clientHeight);
    this.drawBufferSize = vec2.clone(this.viewportSize);

    this.discProgram = createProgram(gl, [discVertShaderSource, discFragShaderSource], null, {
      aModelPosition: 0,
      aModelNormal: 1,
      aModelUvs: 2,
      aInstanceMatrix: 3,
    });

    this.discLocations = {
      aModelPosition: gl.getAttribLocation(this.discProgram, 'aModelPosition'),
      aModelUvs: gl.getAttribLocation(this.discProgram, 'aModelUvs'),
      aInstanceMatrix: gl.getAttribLocation(this.discProgram, 'aInstanceMatrix'),
      uWorldMatrix: gl.getUniformLocation(this.discProgram, 'uWorldMatrix'),
      uViewMatrix: gl.getUniformLocation(this.discProgram, 'uViewMatrix'),
      uProjectionMatrix: gl.getUniformLocation(this.discProgram, 'uProjectionMatrix'),
      uCameraPosition: gl.getUniformLocation(this.discProgram, 'uCameraPosition'),
      uScaleFactor: gl.getUniformLocation(this.discProgram, 'uScaleFactor'),
      uRotationAxisVelocity: gl.getUniformLocation(this.discProgram, 'uRotationAxisVelocity'),
      uTex: gl.getUniformLocation(this.discProgram, 'uTex'),
      uFrames: gl.getUniformLocation(this.discProgram, 'uFrames'),
      uItemCount: gl.getUniformLocation(this.discProgram, 'uItemCount'),
      uAtlasSize: gl.getUniformLocation(this.discProgram, 'uAtlasSize'),
      uIsDarkTheme: gl.getUniformLocation(this.discProgram, 'uIsDarkTheme'),
    };

    this.discGeo = new DiscGeometry(56, 1);
    this.discBuffers = this.discGeo.data;
    this.discVAO = makeVertexArray(
      gl,
      [
        [makeBuffer(gl, this.discBuffers.vertices, gl.STATIC_DRAW), this.discLocations.aModelPosition, 3],
        [makeBuffer(gl, this.discBuffers.uvs, gl.STATIC_DRAW), this.discLocations.aModelUvs, 2],
      ],
      this.discBuffers.indices
    );

    // Inizializza l'icosaedro con il nuovo metodo
    this.#initIcosahedron();
    
    // Inizializza la matrice del mondo
    this.worldMatrix = mat4.create();
    
    this.#initTexture();
    this.#initDiscInstances(this.DISC_INSTANCE_COUNT);

    this.control = new ArcballControl(this.canvas, (deltaTime) => this.#onControlUpdate(deltaTime));

    // Inizializza la vista per guardare direttamente al primo elemento
    if (this.instancePositions.length > 0) {
      const firstVertexPos = this.instancePositions[0];
      const firstVertexDir = vec3.normalize(vec3.create(), firstVertexPos);
      this.control.snapTargetDirection = firstVertexDir;
    }

    if (onInit) onInit(this);
  }
  
  #initIcosahedron() {
    // Crea l'icosaedro base
    this.icoGeo = new IcosahedronGeometry();
    
    // Determina il livello di suddivisione in base al numero di texture
    // Più texture richiedono più punti nell'icosaedro
    let subdivisionLevel = 1; // Livello base di suddivisione
    
    const texturesCount = this.items.length;
    if (texturesCount <= 12) {
      // Per poche texture usa l'icosaedro base (12 vertici)
      subdivisionLevel = 0;
    } else if (texturesCount <= 42) {
      // Per un numero medio di texture usa una suddivisione (42 vertici)
      subdivisionLevel = 1;
    } else if (texturesCount <= 162) {
      // Per molte texture usa due suddivisioni (162 vertici)
      subdivisionLevel = 2;
    } else {
      // Per un numero molto elevato di texture usa tre suddivisioni (642 vertici)
      subdivisionLevel = 3;
    }
    
    // Applica la suddivisione e la sfericizzazione
    this.icoGeo.subdivide(subdivisionLevel).spherize(this.SPHERE_RADIUS);
    
    // Ottieni le posizioni dei vertici dall'icosaedro
    this.instancePositions = this.icoGeo.vertices.map((v) => v.position);
    
    // Se abbiamo più vertici di texture, riduciamo il numero di vertici per evitare spazi vuoti
    if (this.instancePositions.length > texturesCount && texturesCount > 0) {
      this.instancePositions = this.instancePositions.slice(0, texturesCount);
    }
    
    // Assicurati che il primo vertice sia quello che guarda verso "davanti" (z negativo)
    this.#optimizeVertexPositions();
    
    // Aggiorna il conteggio delle istanze
    this.DISC_INSTANCE_COUNT = Math.min(this.instancePositions.length, texturesCount || 1);
    
    console.log(`InfiniteMenu: Created icosahedron with ${this.DISC_INSTANCE_COUNT} vertices for ${texturesCount} textures`);
  }

  #initTexture() {
    const gl = this.gl;
    this.tex = createAndSetupTexture(gl, gl.LINEAR, gl.LINEAR, gl.CLAMP_TO_EDGE, gl.CLAMP_TO_EDGE);

    const itemCount = Math.max(1, this.items.length);
    this.atlasSize = Math.ceil(Math.sqrt(itemCount));
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const cellSize = 512;

    canvas.width = this.atlasSize * cellSize;
    canvas.height = this.atlasSize * cellSize;
    
    // Fill with black background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Track if all images have been loaded
    let loadedCount = 0;
    const totalItems = this.items.length;
    
    console.log(`InfiniteMenu: Initializing texture atlas with ${this.items.length} items, atlas size: ${this.atlasSize}x${this.atlasSize}`);

    Promise.all(this.items.map((item, index) =>
      new Promise(resolve => {
        console.log(`Loading image ${index}:`, item.image);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          loadedCount++;
          console.log(`Image ${index} loaded (${loadedCount}/${totalItems})`);
          resolve(img);
        };
        
        img.onerror = () => {
          console.error(`Failed to load image ${index}:`, item.image);
          
          // Check if there's a fallback image available
          if (item.fallbackImage) {
            console.log(`Trying fallback image for ${index}:`, item.fallbackImage);
            const fallbackImg = new Image();
            fallbackImg.crossOrigin = 'anonymous';
            
            fallbackImg.onload = () => {
              loadedCount++;
              console.log(`Fallback image ${index} loaded (${loadedCount}/${totalItems})`);
              resolve(fallbackImg);
            };
            
            fallbackImg.onerror = () => {
              console.error(`Failed to load fallback image ${index}:`, item.fallbackImage);
              createFallbackCanvas();
            };
            
            fallbackImg.src = item.fallbackImage;
          } else {
            createFallbackCanvas();
          }
          
          // Function to create a fallback colored canvas
          function createFallbackCanvas() {
            // Create a fallback colored rectangle
            const fallbackCanvas = document.createElement('canvas');
            fallbackCanvas.width = cellSize;
            fallbackCanvas.height = cellSize;
            const fallbackCtx = fallbackCanvas.getContext('2d');
            
            // Extract color from URL if it's a singlecolorimage
            let color = '#cccccc'; // default gray
            const imageUrl = item.fallbackImage || item.image;
            if (imageUrl.includes('singlecolorimage.com/get/')) {
              const colorMatch = imageUrl.match(/get\/([0-9a-f]+)/i);
              if (colorMatch && colorMatch[1]) {
                color = `#${colorMatch[1]}`;
              }
            }
            
            fallbackCtx.fillStyle = color;
            fallbackCtx.fillRect(0, 0, cellSize, cellSize);
            
            // Add text if the item has a title
            if (item.title) {
              fallbackCtx.font = '30px Arial';
              fallbackCtx.textAlign = 'center';
              fallbackCtx.textBaseline = 'middle';
              fallbackCtx.fillStyle = getContrastColor(color);
              const words = item.title.split(' ');
              if (words.length > 2) {
                fallbackCtx.fillText(words.slice(0, 2).join(' '), cellSize/2, cellSize/2 - 20);
                fallbackCtx.fillText(words.slice(2).join(' '), cellSize/2, cellSize/2 + 20);
              } else {
                fallbackCtx.fillText(item.title, cellSize/2, cellSize/2);
              }
            }
            
            const canvasImg = new Image();
            canvasImg.src = fallbackCanvas.toDataURL();
            canvasImg.onload = () => {
              loadedCount++;
              console.log(`Fallback canvas image ${index} created (${loadedCount}/${totalItems})`);
              resolve(canvasImg);
            };
          }
        };
        
        img.src = item.image;
      })
    )).then(images => {
      console.log("All images loaded, drawing atlas");
      images.forEach((img, i) => {
        const x = (i % this.atlasSize) * cellSize;
        const y = Math.floor(i / this.atlasSize) * cellSize;
        ctx.drawImage(img, x, y, cellSize, cellSize);
      });

      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
      gl.generateMipmap(gl.TEXTURE_2D);
      console.log("Texture atlas generated and bound");
    }).catch(error => {
      console.error("Error loading images:", error);
    });
    
    // Helper function to determine contrasting text color
    function getContrastColor(hexColor) {
      // Remove # if present
      hexColor = hexColor.replace('#', '');
      
      // Parse the color
      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);
      
      // Calculate luminance - formula from WCAG 2.0
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      // Return black for bright colors, white for dark ones
      return luminance > 0.5 ? '#000000' : '#ffffff';
    }
  }

  #initDiscInstances(count) {
    const gl = this.gl;
    this.discInstances = {
      matricesArray: new Float32Array(count * 16),
      matrices: [],
      buffer: gl.createBuffer(),
    };
    for (let i = 0; i < count; ++i) {
      const instanceMatrixArray = new Float32Array(this.discInstances.matricesArray.buffer, i * 16 * 4, 16);
      instanceMatrixArray.set(mat4.create());
      this.discInstances.matrices.push(instanceMatrixArray);
    }
    gl.bindVertexArray(this.discVAO);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.discInstances.matricesArray.byteLength, gl.DYNAMIC_DRAW);
    const mat4AttribSlotCount = 4;
    const bytesPerMatrix = 16 * 4;
    for (let j = 0; j < mat4AttribSlotCount; ++j) {
      const loc = this.discLocations.aInstanceMatrix + j;
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(
        loc,
        4,
        gl.FLOAT,
        false,
        bytesPerMatrix,
        j * 4 * 4
      );
      gl.vertexAttribDivisor(loc, 1);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);
  }

  #animate(deltaTime) {
    const gl = this.gl;
    if (!gl || !this.control || !this.instancePositions || !this.discInstances?.matrices) {
      return; // Evita errori se qualsiasi oggetto necessario non è inizializzato
    }
    
    // Gestisci l'animazione di interpolazione verso la posizione iniziale
    if (this.#isAnimatingToInitial) {
      // Incrementa il progresso dell'animazione
      this.#animationProgress += deltaTime;
      
      // Calcola il fattore di interpolazione (da 0 a 1) con una funzione di easing
      const t = Math.min(1, this.#animationProgress / this.#animationDuration);
      // Funzione easeOutCubic per un movimento più naturale
      const easedT = 1 - Math.pow(1 - t, 3);
      
      // Interpola tra l'orientamento iniziale e quello target
      quat.slerp(
        this.control.orientation,
        this.#startOrientation,
        this.#targetOrientation,
        easedT
      );
      
      // Termina l'animazione quando è completa
      if (t >= 1) {
        this.#isAnimatingToInitial = false;
        
        // Notifica il cambio di movimento (terminato)
        if (this.onMovementChange) {
          this.onMovementChange(false);
        }
      }
    } else {
      // Aggiornamento normale quando non c'è animazione
    this.control.update(deltaTime, this.TARGET_FRAME_DURATION);
    }

    try {
      // Assicurati che ci siano posizioni valide
      if (this.instancePositions.length === 0) return;
      
      let positions = this.instancePositions.map((p) => {
        if (!p || !this.control.orientation) return vec3.create();
        return vec3.transformQuat(vec3.create(), p, this.control.orientation);
      });
      
    const baseScale = 0.25;
    const SCALE_INTENSITY = 0.6;
    
    positions.forEach((p, ndx) => {
        // Controlla se la matrice esiste
        if (!this.discInstances?.matrices?.[ndx]) return;
        
      // Parte 1: Scala basata sulla profondità (come prima)
      const depthScale = (Math.abs(p[2]) / this.SPHERE_RADIUS) * SCALE_INTENSITY + (1 - SCALE_INTENSITY);
      
      // Parte 2: Scala basata sulla similarità
        // Assicurati che l'indice sia valido per evitare ripetizioni
        const itemIndex = ndx < this.items.length ? ndx : 0;
      // Ottieni il valore di similarità se disponibile, altrimenti usa 0.5 come valore predefinito
      const similarity = this.items[itemIndex]?.similarity !== undefined ? this.items[itemIndex].similarity : 0.5;
      
      // Calcola un fattore di scala basato sulla similarità (valori più alti = punti più grandi)
      // Mappatura da 0-1 a un intervallo ragionevole, es. 0.6-1.4
      const similarityScale = 0.6 + (similarity * 0.8);
      
      // Combina le scale: depthScale per la profondità, similarityScale per la similarità
      const finalScale = depthScale * baseScale * similarityScale;
      
        // Crea la matrice di trasformazione
      const matrix = mat4.create();
      mat4.multiply(matrix, matrix, mat4.fromTranslation(mat4.create(), vec3.negate(vec3.create(), p)));
      mat4.multiply(matrix, matrix, mat4.targetTo(mat4.create(), [0, 0, 0], p, [0, 1, 0]));
      mat4.multiply(matrix, matrix, mat4.fromScaling(mat4.create(), [finalScale, finalScale, finalScale]));
      mat4.multiply(matrix, matrix, mat4.fromTranslation(mat4.create(), [0, 0, -this.SPHERE_RADIUS]));

        // Assicurati che la matrice di destinazione esista prima di copiare
        if (this.discInstances?.matrices?.[ndx]) {
      mat4.copy(this.discInstances.matrices[ndx], matrix);
        }
    });

      if (gl && this.discInstances?.buffer && this.discInstances?.matricesArray) {
    gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.buffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.discInstances.matricesArray);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
      }

    this.smoothRotationVelocity = this.control.rotationVelocity;
    } catch (error) {
      console.error("Error in #animate:", error);
    }
  }

  #render() {
    const gl = this.gl;
    gl.useProgram(this.discProgram);

    // Abilita il blending
    gl.enable(gl.BLEND);
    
    // Configura il blend in base al tema
    if (this.isDarkTheme) {
      // Tema scuro: usa un blend leggermente più additivo per far risaltare gli elementi
      // ONE per la fonte amplifica la luminosità, ONE_MINUS_SRC_ALPHA preserva la trasparenza
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    } else {
      // Tema chiaro: usa un blend standard ma che preserva di più l'opacità
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    }
    gl.blendEquation(gl.FUNC_ADD);

    gl.enable(gl.CULL_FACE);
    gl.enable(gl.DEPTH_TEST);
    
    // Clear con sfondo trasparente
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    gl.uniformMatrix4fv(this.discLocations.uWorldMatrix, false, this.worldMatrix);
    gl.uniformMatrix4fv(this.discLocations.uViewMatrix, false, this.camera.matrices.view);
    gl.uniformMatrix4fv(this.discLocations.uProjectionMatrix, false, this.camera.matrices.projection);
    gl.uniform3f(this.discLocations.uCameraPosition, this.camera.position[0], this.camera.position[1], this.camera.position[2]);
    gl.uniform4f(
      this.discLocations.uRotationAxisVelocity,
      this.control.rotationAxis[0],
      this.control.rotationAxis[1],
      this.control.rotationAxis[2],
      this.smoothRotationVelocity * 1.1
    );

    // Passa il tema allo shader
    gl.uniform1i(this.discLocations.uIsDarkTheme, this.isDarkTheme ? 1 : 0);
    gl.uniform1i(this.discLocations.uItemCount, this.items.length);
    gl.uniform1i(this.discLocations.uAtlasSize, this.atlasSize);

    gl.uniform1f(this.discLocations.uFrames, this.#frames);
    gl.uniform1f(this.discLocations.uScaleFactor, this.scaleFactor);
    gl.uniform1i(this.discLocations.uTex, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);

    gl.bindVertexArray(this.discVAO);
    gl.drawElementsInstanced(
      gl.TRIANGLES,
      this.discBuffers.indices.length,
      gl.UNSIGNED_SHORT,
      0,
      this.DISC_INSTANCE_COUNT
    );
  }

  #updateCameraMatrix() {
    mat4.targetTo(this.camera.matrix, this.camera.position, [0, 0, 0], this.camera.up);
    mat4.invert(this.camera.matrices.view, this.camera.matrix);
  }

  #updateProjectionMatrix(gl) {
    this.camera.aspect = gl.canvas.clientWidth / gl.canvas.clientHeight;
    const height = this.SPHERE_RADIUS * 0.35;
    const distance = this.camera.position[2];
    if (this.camera.aspect > 1) {
      this.camera.fov = 2 * Math.atan(height / distance);
    } else {
      this.camera.fov = 2 * Math.atan((height / this.camera.aspect) / distance);
    }
    mat4.perspective(
      this.camera.matrices.projection,
      this.camera.fov,
      this.camera.aspect,
      this.camera.near,
      this.camera.far
    );
    mat4.invert(this.camera.matrices.inversProjection, this.camera.matrices.projection);
  }

  #onControlUpdate() {
    const timeScale = this.#deltaTime / this.TARGET_FRAME_DURATION + 0.0001;
    let damping = 5 / timeScale;
    let cameraTargetZ = 3;

    const isMoving = this.control.isPointerDown || Math.abs(this.smoothRotationVelocity) > 0.01;

    if (isMoving !== this.movementActive) {
      this.movementActive = isMoving;
      this.onMovementChange(isMoving);
    }

    if (!this.control.isPointerDown) {
      // All'avvio o quando non c'è interazione, trova la texture più vicina al centro
      let nearestVertexIndex;
      
      if (this.firstRunUpdate) {
        // All'avvio, forza la selezione del primo vertice (indice 0)
        nearestVertexIndex = 0;
        this.firstRunUpdate = false;
      } else {
        // Dopo l'avvio, trova il vertice più vicino alla direzione di visualizzazione
        nearestVertexIndex = this.#findNearestVertexIndex();
      }
      
      // Nota: la notifica viene già gestita in findNearestVertexIndex
      
      const snapDirection = vec3.normalize(vec3.create(), this.#getVertexWorldPosition(nearestVertexIndex));
      this.control.snapTargetDirection = snapDirection;
    }
    else {
      cameraTargetZ += this.control.rotationVelocity * 80 + 2.5;
      damping = 7 / timeScale;
    }

    this.camera.position[2] += (cameraTargetZ - this.camera.position[2]) / damping;
    this.#updateCameraMatrix();
  }

  #findNearestVertexIndex() {
    const n = this.control.snapDirection;
    const inversOrientation = quat.conjugate(quat.create(), this.control.orientation);
    const nt = vec3.transformQuat(vec3.create(), n, inversOrientation);

    let maxD = -1;
    let nearestVertexIndex;
    for (let i = 0; i < this.instancePositions.length; ++i) {
      const d = vec3.dot(nt, this.instancePositions[i]);
      if (d > maxD) {
        maxD = d;
        nearestVertexIndex = i;
      }
    }
    
    // Se è cambiata la texture centrale, emetti l'evento
    if (this.nearestVertexIndex !== nearestVertexIndex) {
      this.nearestVertexIndex = nearestVertexIndex;
      const itemIndex = nearestVertexIndex % Math.max(1, this.items.length);
      this.onActiveItemChange(itemIndex);
    }
    
    return nearestVertexIndex;
  }

  #getVertexWorldPosition(index) {
    // Verifica che l'indice sia valido e che l'array instancePositions esista
    if (index === undefined || !this.instancePositions || index >= this.instancePositions.length) {
      console.warn('Invalid vertex index or instance positions array', index);
      return vec3.create(); // Restituisce un vettore vuoto per evitare errori
    }
    
    const nearestVertexPos = this.instancePositions[index];
    if (!nearestVertexPos || !this.control?.orientation) {
      console.warn('Invalid vertex position or orientation', index);
      return vec3.create(); // Restituisce un vettore vuoto per evitare errori
    }
    
    return vec3.transformQuat(vec3.create(), nearestVertexPos, this.control.orientation);
  }

  // Modifica il metodo centerOnInitialTexture per aggiungere l'animazione
  centerOnInitialTexture() {
    if (!this.control) return;
    
    // Memorizza l'orientamento di partenza
    this.#startOrientation = quat.clone(this.control.orientation);
    
    // Crea l'orientamento target (identità, che guarda verso Z negativo)
    this.#targetOrientation = quat.create();
    
    // Imposta i parametri dell'animazione
    this.#isAnimatingToInitial = true;
    this.#animationProgress = 0;
    
    // Forza l'attivazione dell'elemento iniziale subito
    const initialTextureIndex = 0;
    if (this.onActiveItemChange && initialTextureIndex < this.items.length) {
      this.onActiveItemChange(initialTextureIndex);
    }
    
    // Notifica il cambio di movimento (attivato)
    if (this.onMovementChange) {
      this.onMovementChange(true);
    }
  }

  // Metodo pubblico per ottimizzare il layout delle texture
  optimizeLayout() {
    this.#optimizeVertexPositions();
  }

  #optimizeVertexPositions() {
    if (this.instancePositions.length === 0 || this.items.length === 0) return;

    // Sort positions based on similarity (items are already sorted by similarity)
    const sortedPositions = this.instancePositions.sort((a, b) => {
      const distA = vec3.distance(a, [0, 0, -1]);
      const distB = vec3.distance(b, [0, 0, -1]);
      return distA - distB;
    });

    // Assign sorted positions to items based on similarity
    this.instancePositions = sortedPositions;
  }
}

const defaultItems = [
  {
    image: 'https://picsum.photos/900/900?grayscale',
    link: 'https://google.com/',
    title: '',
    description: ''
  }
];

export default React.forwardRef(function InfiniteMenu({ 
  items = [], 
  onItemClick, 
  initialCenteredItem,
  isUITransitioning = false,
  lastActiveMenu = null,
  isDark = false,
  onCenterChange = null
}, ref) {
  const canvasRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);
  const [isMoving, setIsMoving] = useState(false);
  const initializedRef = useRef(false);
  const sketchRef = useRef(null);
  
  // Riferimento all'ultimo elemento attivo per le notifiche di cambiamento
  const lastActiveItemRef = useRef(null);
  // Variabile per tracciare se l'utente sta interagendo
  const interactionTimerRef = useRef(null);
  
  // Aggiorniamo il riferimento all'ultimo item attivo quando cambia
  useEffect(() => {
    if (activeItem && !isUITransitioning) {
      // Confronta con l'elemento precedente per evitare notifiche multiple inutili
      if (!lastActiveItemRef.current || lastActiveItemRef.current.title !== activeItem.title) {
        // Notifica al parent component che è cambiato l'elemento centrale
        if (onCenterChange && !isMoving) {
          onCenterChange(activeItem);
        }
      lastActiveItemRef.current = activeItem;
    }
    }
  }, [activeItem, isUITransitioning, isMoving, onCenterChange]);

  // Monitora il cambiamento dello stato di movimento
  useEffect(() => {
    if (activeItem) {
      // Quando l'utente smette di muovere l'icosaedro
      if (!isMoving) {
        // Notifica il parent del cambio di focus
        if (onCenterChange) {
          console.log("Movement stopped, notifying parent of focused item:", activeItem.title);
          onCenterChange(activeItem);
        }
      }
    }
  }, [isMoving, activeItem, onCenterChange]);

  // Funzione per tornare alla texture centrale
  const handleCenterView = useCallback(() => {
    if (sketchRef.current) {
      sketchRef.current.centerOnInitialTexture();
    }
  }, []);
  
  // Cleanup del timer
  useEffect(() => {
    return () => {
      if (interactionTimerRef.current) {
        clearTimeout(interactionTimerRef.current);
      }
    };
  }, []);
  
  // Esportiamo l'istanza all'esterno tramite il ref
  useEffect(() => {
    if (ref) {
      ref(sketchRef.current ? { 
        sketchRef, 
        centerOnInitialTexture: handleCenterView 
      } : null);
    }
  }, [ref, sketchRef.current, handleCenterView]);

  // Funzione per gestire il cambio dell'elemento attivo
  const handleActiveItem = useCallback((index) => {
      const itemArray = items.length ? items : defaultItems;
      // Assicurati che l'indice sia valido per evitare ripetizioni
      const itemIndex = Math.min(index, itemArray.length - 1);
    
    // Aggiorna lo stato attivo
      setActiveItem(itemArray[itemIndex]);
    
    // Notifica immediatamente il parent del cambio di focus quando si ferma su una texture
    if (onCenterChange && !isMoving) {
      onCenterChange(itemArray[itemIndex]);
    }
  }, [items, isMoving, onCenterChange]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    let sketch;

    if (canvas) {
      console.log("Initializing InfiniteMenu with items:", items.length);
      
      // Limita il numero di elementi visualizzati al numero di elementi unici disponibili
      // Non permettere più ripetizioni di texture nella griglia
      const maxInstances = items.length > 0 ? items.length : defaultItems.length;
      
      sketch = new InfiniteGridMenu(
        canvas,
        items.length ? items : defaultItems,
        handleActiveItem,
        setIsMoving,
        (sk) => {
          // Limita il numero di istanze
          if (sk.instancePositions && sk.instancePositions.length > maxInstances) {
            sk.instancePositions = sk.instancePositions.slice(0, maxInstances);
          }
          
          // Ottimizza il posizionamento iniziale se c'è una texture selezionata
          if (initialCenteredItem && initialCenteredItem.id) {
            const selectedIndex = items.findIndex(item => item.isSelected);
            if (selectedIndex >= 0) {
              // Posiziona il punto di vista in modo che la texture selezionata sia frontale e centrata
              console.log("Posizionamento iniziale ottimizzato per texture:", initialCenteredItem.title);
              
              // Riordina le posizioni dei vertici in modo che la texture selezionata sia frontale
              // Nota: non possiamo accedere direttamente a metodi privati della classe
              // ma possiamo invocare metodi pubblici che hanno lo stesso effetto
              if (typeof sk.optimizeLayout === 'function') {
                sk.optimizeLayout();
              }
            }
          }
          
          sk.run();
          
          // Auto-centra sulla texture iniziale dopo il primo rendering
          setTimeout(() => {
            sk.centerOnInitialTexture();
          }, 100);
        },
        isDark  // Passa lo stato del tema all'InfiniteGridMenu
      );
      
      // Salva il riferimento allo sketch
      sketchRef.current = sketch;
      
      // Forza l'attivazione del primo elemento (texture selezionata) all'avvio
      if (!initializedRef.current && items.length > 0) {
        initializedRef.current = true;
        handleActiveItem(0);
      }
    }

    const handleResize = () => {
      if (sketch) {
        sketch.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      initializedRef.current = false;
    };
  }, [items, initialCenteredItem, isDark]);

  // Forza l'attivazione del primo elemento all'avvio
  useEffect(() => {
    if (items.length > 0 && !activeItem) {
      // Attiva il primo elemento della lista (texture selezionata)
      setActiveItem(items[0]);
    }
  }, [items, activeItem]);

  // Funzione per gestire il click sul bottone di navigazione (freccia in alto a destra)
  const handleNavigateButtonClick = () => {
    if (!activeItem?.link) return;
    
    if (onItemClick) {
      // Use custom click handler if provided, ma con un flag per indicare che è una navigazione
      onItemClick(activeItem, 'navigate');
    } else if (activeItem.link.startsWith('http')) {
      // Open external links in new tab
      window.open(activeItem.link, '_blank');
    } else {
      // Log internal routes
      console.log('Internal route:', activeItem.link);
    }
  };
  
  // Funzione per gestire il click sul bottone di rigenerazione (bottone blu)
  const handleRegenerateButtonClick = () => {
    if (!activeItem || activeItem.isSelected) return; // Non rigenerare se è già selezionato
    
    if (onItemClick) {
      // Use custom click handler if provided, ma con un flag per indicare che è una rigenerazione
      onItemClick(activeItem, 'regenerate');
    }
  };

  // Aggiungi un style tag per sovrascrivere le animazioni globali
  useEffect(() => {
    // Crea un elemento style
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .no-hover-effect {
        transform: none !important;
        animation: none !important;
        position: static !important;
      }
      .no-hover-effect:hover, .no-hover-effect:active, .no-hover-effect:focus {
        transform: none !important;
        box-shadow: none !important;
        border: none !important;
        position: static !important;
      }
      .action-button {
        transition: background-color 0.2s ease, color 0.2s ease !important;
      }
    `;
    
    // Aggiungilo al document head
    document.head.appendChild(styleElement);
    
    // Clean up quando il componente viene smontato
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas
        id="infinite-grid-menu-canvas"
        ref={canvasRef}
        style={{
          background: 'transparent',
          mixBlendMode: 'normal'
        }}
      />

      {/* Mostra il numero totale di texture visualizzate */}
      <div className="total-textures">
        Visualizzando {items.length} texture
      </div>

      {/* Feel free to customize what's displayed when the menu is not being dragged and an item is displayed in the center */}
      {(activeItem || (isUITransitioning && lastActiveItemRef.current)) && (
        <>
          <div className="buttons-container" style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            transition: 'all 0.2s ease'
          }}>
            <ButtonGroup vertical scale={1} style={{
              borderRadius: '50px',
              overflow: 'hidden',
              background: isDark ? '#333' : '#fff',
              border: isDark ? '1px solid #444' : '1px solid #eaeaea',
              boxShadow: isDark 
                ? '0 0 15px rgba(0, 0, 0, 0.4), 0 0 8px rgba(0, 0, 0, 0.2)' 
                : '0 8px 16px rgba(0, 0, 0, 0.1)',
              padding: '5px',
              transition: 'all 0.2s ease'
            }}>
              {/* Bottone di navigazione (freccia in alto a destra) */}
              <Button 
                onClick={!isUITransitioning ? handleNavigateButtonClick : undefined} 
                className="action-button navigate-button no-hover-effect"
                title="Vai alla scheda dettagliata della texture"
                icon={<span style={{ fontSize: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>&#x2197;</span>}
                iconRight={false}
                auto
                type="default"
                style={{
                  ...noHoverEffectStyle,
                  borderRadius: '50px',
                  opacity: isMoving || isUITransitioning ? 0.5 : 1,
                  margin: '2px 0',
                  padding: '0px',
                  border: 'none',
                  height: '40px',
                  width: '40px',
                  minWidth: '40px',
                  background: isDark ? '#444' : '#eaeaea',
                  color: isDark ? '#eaeaea' : '#333',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'background-color 0.2s ease, color 0.2s ease'
                }}
              />
              
              {/* Bottone di rigenerazione */}
              <Button
                onClick={!isUITransitioning && !(isUITransitioning ? lastActiveItemRef.current?.isSelected : activeItem?.isSelected) ? handleRegenerateButtonClick : undefined} 
                className={`action-button regenerate-button ${isMoving || isUITransitioning || (isUITransitioning ? lastActiveItemRef.current?.isSelected : activeItem?.isSelected) ? 'inactive' : 'active'} no-hover-effect`}
                title="Rigenera il grafico con questa texture come centro"
                icon={<span style={{ fontSize: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>&#x21BB;</span>}
                iconRight={false}
                auto
                type="default"
                disabled={(isUITransitioning ? lastActiveItemRef.current?.isSelected : activeItem?.isSelected)}
                style={{
                  ...noHoverEffectStyle,
                  borderRadius: '50px',
                  opacity: isMoving || isUITransitioning || (isUITransitioning ? lastActiveItemRef.current?.isSelected : activeItem?.isSelected) ? 0.3 : 1,
                  margin: '2px 0',
                  padding: '0px',
                  border: 'none',
                  height: '40px',
                  width: '40px',
                  minWidth: '40px',
                  background: isDark ? '#444' : '#eaeaea',
                  color: isDark ? '#eaeaea' : '#333',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'background-color 0.2s ease, color 0.2s ease'
                }}
              />
              
              {/* Bottone per tornare alla texture centrale */}
              <Button 
                onClick={!isUITransitioning ? handleCenterView : undefined} 
                className={`action-button home-button ${isMoving || isUITransitioning ? 'inactive' : 'active'} ${(isUITransitioning ? lastActiveItemRef.current?.isSelected : activeItem?.isSelected) ? 'selected-item-button' : ''} no-hover-effect`}
                title="Torna alla texture selezionata iniziale"
                icon={<span style={{ fontSize: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>&#x2302;</span>}
                iconRight={false}
                auto
                type={(isUITransitioning ? lastActiveItemRef.current?.isSelected : activeItem?.isSelected) ? 'secondary' : 'default'}
                style={{
                  ...noHoverEffectStyle,
                  borderRadius: '50px',
                  opacity: isMoving || isUITransitioning ? 0.5 : 1,
                  margin: '2px 0',
                  padding: '0px',
                  border: 'none',
                  height: '40px',
                  width: '40px',
                  minWidth: '40px',
                  background: (isUITransitioning ? lastActiveItemRef.current?.isSelected : activeItem?.isSelected)
                    ? (isDark ? '#666' : '#333')
                    : (isDark ? '#444' : '#eaeaea'),
                  color: (isUITransitioning ? lastActiveItemRef.current?.isSelected : activeItem?.isSelected)
                    ? '#fff'
                    : (isDark ? '#eaeaea' : '#333'),
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'background-color 0.2s ease, color 0.2s ease'
                }}
              />
            </ButtonGroup>
          </div>
        </>
      )}
    </div>
  );
})
